import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  TemplateRef,
  Type,
  ViewContainerRef,
  contentChild,
  effect,
  input,
  signal,
  viewChild,
} from "@angular/core";
import invariant from "tiny-invariant";

@Component({
  standalone: true,
  selector: "standard-lazy-component",
  template: `
    @if (!componentRef() && skeletonTemplate()) {
      <ng-container *ngTemplateOutlet="skeletonTemplate()!" />
    }
    <ng-template #lazyContent />
  `,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardLazyComponent<
  TContent,
  TLazyProps extends Record<string, unknown>,
> {
  readonly content = input.required<Type<TContent> | undefined>();
  readonly lazyProps = input.required<TLazyProps | undefined>();

  readonly skeletonTemplate = contentChild<TemplateRef<never>>("skeleton");
  readonly lazyContent = viewChild.required("lazyContent", {
    read: ViewContainerRef,
  });

  readonly componentRef = signal<ComponentRef<TContent> | null>(null);

  constructor() {
    effect(() => {
      const content = this.content();

      // Already set up
      if (this.componentRef()) {
        return;
      }

      const lazyProps = this.lazyProps();
      invariant(lazyProps);

      if (content) {
        void this.render(content, lazyProps);
      }
    });
  }

  async render(ContentComponent: Type<TContent>, lazyProps: TLazyProps) {
    let componentRef = this.componentRef();
    if (!componentRef) {
      const lazyContent = this.lazyContent();
      componentRef = lazyContent.createComponent(ContentComponent);
    }

    const entries = Object.entries(lazyProps);
    for (const [key, val] of entries) {
      // Set to new value if it's set
      componentRef.setInput(key, val);
    }

    this.componentRef.set(componentRef);
  }
}
